import { Box, Button } from "@mui/material";
import I18n from "components/materials/I18n";
import { ModelId } from "models/common/Model";
import {
  Co2EvolutionWidget,
  DateBoundary,
  FixDateBoundary,
  RelativeDateBoundary,
  retrieveFixDateBoundary,
  retrieveRelativeDateBoundary,
} from "models/widgets/Widget";
import { memo, useCallback, useState } from "react";
import { Nullable } from "types/common";
import * as styles from "./styles";
import RelativeOrFixDateRangeInput from "components/element/inputs/RelativeOrFixDateRangeInput";
import { DayJsFormat } from "utils/DayjsUtils";
import DateUtils from "utils/DateUtils";
import useValidation from "./useValidation";
import useSensors from "hooks/queries/useSensors";
import SensorsInputMulti from "components/element/inputs/SensorsInputMulti";
import useUser from "hooks/queries/useUser";
import useFetchedApplicationConfig from "hooks/useFetchedApplicationConfig";

type Props = {
  properties: Co2EvolutionWidget["properties"];
  onSubmit: (properties: Co2EvolutionWidget["properties"]) => void;
  onPropertiesChange?: (
    properties: Nullable<Co2EvolutionWidget["properties"]>
  ) => void;
};

function EditionForm({ properties, onSubmit, onPropertiesChange }: Props) {
  const { applicationConfig } = useFetchedApplicationConfig();
  const { userLaboratoryId } = useUser();
  const { sensors } = useSensors({
    queryParams: {
      laboratoryId: userLaboratoryId,
    },
  });
  const [propertiesState, setPropertiesState] =
    useState<Nullable<Co2EvolutionWidget["properties"]>>(properties);

  const handleDateTypeChange = useCallback(
    (dateType: keyof FixDateBoundary | keyof RelativeDateBoundary) => {
      let updatedProperties: Nullable<Co2EvolutionWidget["properties"]> =
        propertiesState;

      if (dateType === "fix") {
        updatedProperties = {
          ...propertiesState,
          fromDate: propertiesState.fromDate
            ? { fix: retrieveFixDateBoundary(propertiesState.fromDate) }
            : null,
          toDate: propertiesState.toDate
            ? { fix: retrieveFixDateBoundary(propertiesState.toDate) }
            : null,
        };
      }

      if (dateType === "relative") {
        updatedProperties = {
          ...propertiesState,
          fromDate: propertiesState.fromDate
            ? {
                relative: retrieveRelativeDateBoundary(
                  propertiesState.fromDate
                ),
              }
            : null,
          toDate: propertiesState.toDate
            ? { relative: retrieveRelativeDateBoundary(propertiesState.toDate) }
            : null,
        };
      }
      setPropertiesState(updatedProperties);
      onPropertiesChange?.(updatedProperties);
    },
    [onPropertiesChange, propertiesState]
  );

  const handleFromDateChange = useCallback(
    (fromDate: DateBoundary | null) => {
      const updatedProperties = {
        ...propertiesState,
        ...(fromDate !== null && {
          fromDate,
        }),
      };
      setPropertiesState(updatedProperties);
      onPropertiesChange?.(updatedProperties);
    },
    [onPropertiesChange, propertiesState]
  );

  const handleToDateChange = useCallback(
    (toDate: DateBoundary | null) => {
      const updatedProperties = {
        ...propertiesState,
        ...(toDate !== null && {
          toDate,
        }),
      };
      setPropertiesState(updatedProperties);
      onPropertiesChange?.(updatedProperties);
    },
    [onPropertiesChange, propertiesState]
  );

  const handleSensorsChange = useCallback(
    (sensorIds: ModelId[] | null) => {
      const updatedProperties = {
        ...propertiesState,
        sensorIds,
      };
      setPropertiesState(updatedProperties);
      onPropertiesChange?.(updatedProperties);
    },
    [onPropertiesChange, propertiesState]
  );

  const nowUTC = DateUtils.nowUTC();
  const minDate = applicationConfig.dashboardResultsMinDate ?? undefined;
  // We want to disable the future
  const maxDate = DateUtils.min(
    applicationConfig.dashboardResultsMaxDate ?? nowUTC,
    nowUTC
  );

  const { validateProperties, errors } = useValidation({
    properties: propertiesState,
    nowUTC,
    minDate,
    maxDate,
  });

  const submit = useCallback(() => {
    if (!propertiesState.fromDate || !propertiesState.toDate) {
      return;
    }

    const { validatedProperties } = validateProperties(propertiesState);
    if (!validatedProperties) {
      return;
    }

    onSubmit(validatedProperties);
  }, [onSubmit, propertiesState, validateProperties]);

  return (
    <Box sx={styles.root}>
      <SensorsInputMulti
        sensors={sensors ?? []}
        onChange={handleSensorsChange}
        values={propertiesState?.sensorIds ?? []}
        errors={errors.sensorIdErrors}
      />

      <RelativeOrFixDateRangeInput
        onDateTypeChange={handleDateTypeChange}
        fromDate={propertiesState.fromDate}
        toDate={propertiesState.toDate}
        onFromDateChange={handleFromDateChange}
        onToDateChange={handleToDateChange}
        minDate={minDate}
        maxDate={maxDate}
        fromDateFormat={DayJsFormat.DayMonthYearHourMinute}
        toDateFormat={DayJsFormat.DayMonthYearHourMinute}
        withTime={true}
        fromRelativeDateInputErrors={errors.fromRelativeDateInputErrors}
        toRelativeDateInputErrors={errors.toRelativeDateInputErrors}
        toFixDateInputErrors={errors.toFixDateInputErrors}
      />

      <Button onClick={submit} variant="contained" sx={styles.submitButton}>
        <I18n map="general_text.validate" />
      </Button>
    </Box>
  );
}

export default memo(EditionForm);
